<template>
    <div class="analytics-trend-chart">
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-8">
            <div class="inline-flex items-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span class="text-sm text-gray-600">Loading chart data...</span>
            </div>
        </div>

        <!-- Error State -->
        <div v-else-if="errorMessage" class="text-center py-8">
            <svg class="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">{{ errorTitle }}</h3>
            <p class="mt-1 text-sm text-gray-500">{{ errorMessage }}</p>
            <div class="mt-4">
                <button type="button" @click="loadChartData" class="text-sm text-blue-600 hover:text-blue-500">
                    Try Again
                </button>
            </div>
        </div>

        <!-- Chart Container (always rendered, but hidden during loading/error) -->
        <div class="relative bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div id="analytics-trend-chart"
                 ref="chartContainer"
                 :style="graphHeight + (isLoading || errorMessage ? 'visibility: hidden;' : 'visibility: visible;')"
                 class="w-full">
            </div>

            <!-- Chart title overlay -->
            <div class="absolute top-2 left-2 bg-white bg-opacity-90 px-3 py-1 rounded-md shadow-sm">
                <span class="text-sm font-semibold text-gray-700">{{ chartTitle }}</span>
            </div>

            <!-- Chart Statistics (performance view) -->
            <div v-if="chartStats && !isLoading && !errorMessage && !insightsSimplified" class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-4">
                <div class="bg-blue-50 p-3 rounded-lg text-center">
                    <div class="text-lg font-bold text-blue-600">{{ chartStats.min.toFixed(4) }}</div>
                    <div class="text-xs text-blue-800">Minimum</div>
                </div>
                <div class="bg-green-50 p-3 rounded-lg text-center">
                    <div class="text-lg font-bold text-green-600">{{ chartStats.max.toFixed(4) }}</div>
                    <div class="text-xs text-green-800">Maximum</div>
                </div>
                <div class="bg-purple-50 p-3 rounded-lg text-center">
                    <div class="text-lg font-bold text-purple-600">{{ chartStats.avg.toFixed(4) }}</div>
                    <div class="text-xs text-purple-800">Average</div>
                </div>
                <div class="bg-yellow-50 p-3 rounded-lg text-center">
                    <div class="text-lg font-bold text-yellow-600">{{ chartStats.latest.toFixed(4) }}</div>
                    <div class="text-xs text-yellow-800">Latest</div>
                </div>
            </div>

            <!-- Trend Information (only for trend_detection, not MAD; performance view) -->
            <div v-if="chartStats && !isLoading && !errorMessage && metric !== 'volatility_mad' && !insightsSimplified" class="bg-gray-50 p-3 rounded-lg mt-2 text-center">
                <div class="text-sm font-medium text-gray-700">
                    Trend: <span class="font-bold" :class="{
                        'text-green-600': chartStats.trend.includes('Upward'),
                        'text-red-600': chartStats.trend.includes('Downward'),
                        'text-gray-600': chartStats.trend === 'Stable'
                    }">{{ chartStats.trend }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {amchartsMixin} from "@/mixins/amchartsMixin";

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

export default {
    name: "AnalyticsTrendChart",
    mixins: [SonicuCoreMixin, amchartsMixin],
    props: {
        meterId: {
            type: Number,
            required: true
        },
        metric: {
            type: String,
            default: 'trend_detection'
        }
    },
    data() {
        return {
            isLoading: true,
            errorMessage: '',
            errorTitle: 'Chart Error',
            chartData: [],
            chartStats: null,
            isLoadingData: false,
            insightsSimplified: false,
        }
    },
    created() {
        // Keep root non-reactive — AM5 root manages its own DOM state
        Object.defineProperty(this, 'root', {
            value: null,
            writable: true,
            enumerable: false,
            configurable: true,
        });

        this.initializeAM5Charts();
    },
    mounted() {
        this.bindInsightsMode();
        this.$nextTick(() => {
            this.initializeChart();
            this.loadChartData();
        });
    },
    beforeUnmount() {
        if (this._insightsModeHandler) {
            window.removeEventListener('advanced-analytics-insights-mode', this._insightsModeHandler);
        }
        if (this.root) {
            this.root.dispose();
            this.root = null;
        }
    },
    methods: {
        bindInsightsMode() {
            const apply = () => {
                this.insightsSimplified =
                    typeof window !== 'undefined' && window.__ADVANCED_ANALYTICS_INSIGHTS_MODE === 'simple';
            };
            apply();
            this._insightsModeHandler = () => apply();
            window.addEventListener('advanced-analytics-insights-mode', this._insightsModeHandler);
        },
        initializeChart() {
            this.root = am5.Root.new(this.$refs.chartContainer);
            this.root.setThemes([am5themes_Animated.new(this.root)]);

            const chart = this.root.container.children.push(
                am5xy.XYChart.new(this.root, {
                    panX: true,
                    panY: false,
                    wheelX: 'panX',
                    wheelY: 'zoomX',
                    pinchZoomX: true,
                    paddingTop: 20,
                    paddingRight: 20,
                    paddingBottom: 20,
                    paddingLeft: 20,
                })
            );

            // X axis (Date)
            const xAxis = chart.xAxes.push(
                am5xy.DateAxis.new(this.root, {
                    baseInterval: { timeUnit: 'day', count: 1 },
                    renderer: am5xy.AxisRendererX.new(this.root, {
                        minGridDistance: 50,
                    }),
                    tooltip: am5.Tooltip.new(this.root, {}),
                })
            );
            xAxis.get('renderer').labels.template.setAll({
                fontSize: 12,
            });

            // Y axis (Values)
            const yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(this.root, {
                    renderer: am5xy.AxisRendererY.new(this.root, {
                        minWidth: 50,
                    }),
                })
            );
            yAxis.get('renderer').labels.template.adapters.add('text', (text) => {
                if (text !== undefined && text !== null) {
                    return parseFloat(text).toFixed(4);
                }
                return text;
            });

            const movingAvgLabel = this.metric === 'volatility_mad' ? '30-Day MAD' : '30-Day Moving Average';
            const dailyAvgLabel = this.metric === 'volatility_mad' ? 'Daily Median' : 'Daily Average';
            const movingAvgTooltip = (this.metric === 'volatility_mad' ? 'MAD' : 'Moving Average') + ': {valueY.formatNumber("#.######")}\nRounded: {moving_average_rounded.formatNumber("#.###")}';
            const dailyAvgTooltip = (this.metric === 'volatility_mad' ? 'Daily Median' : 'Daily Average') + ': {valueY.formatNumber("#.######")}\nRounded: {daily_average_rounded.formatNumber("#.###")}';

            // Moving average series (solid, indigo with fill)
            const movingAvgSeries = chart.series.push(
                am5xy.LineSeries.new(this.root, {
                    name: movingAvgLabel,
                    xAxis,
                    yAxis,
                    valueYField: 'moving_average',
                    valueXField: 'date',
                    tooltip: am5.Tooltip.new(this.root, { labelText: movingAvgTooltip }),
                    fill: am5.color('#4F46E5'),
                    stroke: am5.color('#4F46E5'),
                })
            );
            movingAvgSeries.strokes.template.setAll({ strokeWidth: 2 });
            movingAvgSeries.fills.template.setAll({ fillOpacity: 0.1, visible: true });

            // Daily average series (dashed, green)
            const dailyAvgSeries = chart.series.push(
                am5xy.LineSeries.new(this.root, {
                    name: dailyAvgLabel,
                    xAxis,
                    yAxis,
                    valueYField: 'daily_average',
                    valueXField: 'date',
                    tooltip: am5.Tooltip.new(this.root, { labelText: dailyAvgTooltip }),
                    stroke: am5.color('#10B981'),
                })
            );
            dailyAvgSeries.strokes.template.setAll({
                strokeWidth: 1,
                strokeDasharray: [5, 5],
            });

            // Cursor
            chart.set('cursor', am5xy.XYCursor.new(this.root, {
                behavior: 'zoomX',
                xAxis,
            }));

            // Scrollbar
            chart.set('scrollbarX', am5.Scrollbar.new(this.root, { orientation: 'horizontal' }));

            // Legend — placed below x-axis via bottomAxesContainer
            const legend = chart.bottomAxesContainer.children.push(
                am5.Legend.new(this.root, {
                    centerX: am5.percent(50),
                    x: am5.percent(50),
                    paddingTop: 8,
                })
            );
            legend.data.setAll(chart.series.values);

            // Store refs for data loading
            this._chart = chart;
            this._movingAvgSeries = movingAvgSeries;
            this._dailyAvgSeries = dailyAvgSeries;
        },

        async loadChartData() {
            if (this.isLoadingData) {
                return;
            }

            this.isLoadingData = true;
            this.isLoading = true;
            this.errorMessage = '';

            try {
                const response = await axios.get(
                    this.company.buildCompanyApiInternalRoute('analytics-meter') + '/' + this.meterId,
                    { params: { metric: this.metric } }
                );

                if (response.data.success && response.data.data && response.data.data.length > 0) {
                    const chartData = response.data.data.map(item => {
                        if (this.metric === 'volatility_mad') {
                            return {
                                date: new Date(item.full_date || item.timestamp).getTime(),
                                moving_average: parseFloat(item.raw_mad || item.mad),
                                daily_average: parseFloat(item.raw_daily_median || item.daily_median || item.mad),
                                moving_average_rounded: parseFloat(item.mad),
                                daily_average_rounded: parseFloat(item.daily_median || item.mad),
                            };
                        }
                        return {
                            date: new Date(item.full_date || item.timestamp).getTime(),
                            moving_average: parseFloat(item.raw_moving_avg || item.moving_average),
                            daily_average: parseFloat(item.raw_daily_avg || item.daily_average || item.moving_average),
                            moving_average_rounded: parseFloat(item.moving_average),
                            daily_average_rounded: parseFloat(item.daily_average || item.moving_average),
                        };
                    });

                    this._movingAvgSeries.data.setAll(chartData);
                    this._dailyAvgSeries.data.setAll(chartData);
                    this.chartData = chartData;
                    this.calculateChartStats();
                    this.isLoading = false;
                } else {
                    this.handleError(
                        'No Chart Data Available',
                        response.data.message || 'No data available for the selected period'
                    );
                }
            } catch (error) {
                this.handleError(
                    'Failed to Load Chart',
                    error.response?.data?.message || error.message || 'Failed to load chart data'
                );
            } finally {
                this.isLoadingData = false;
            }
        },

        calculateChartStats() {
            if (!this.chartData || this.chartData.length === 0) {
                this.chartStats = null;
                return;
            }

            const values = this.chartData.map(d => d.moving_average);
            const min = Math.min(...values);
            const max = Math.max(...values);
            const avg = values.reduce((a, b) => a + b, 0) / values.length;
            const latest = values[values.length - 1];
            const first = values[0];

            let trend = 'Stable';
            const percentChange = ((latest - first) / first) * 100;
            if (Math.abs(percentChange) < 0.1) {
                trend = 'Stable';
            } else if (latest > first) {
                trend = `Upward (+${percentChange.toFixed(2)}%)`;
            } else {
                trend = `Downward (${percentChange.toFixed(2)}%)`;
            }

            this.chartStats = { min, max, avg, latest, trend };
        },

        handleError(title, message) {
            this.isLoading = false;
            this.isLoadingData = false;
            this.errorTitle = title;
            this.errorMessage = message;
            this.chartStats = null;
        },
    },
    computed: {
        graphHeight() {
            let clientHeight = document.documentElement.clientHeight;
            clientHeight = Math.max(clientHeight - 400, 450);
            return 'height: ' + clientHeight.toString() + 'px; width: 100%;';
        },
        chartTitle() {
            if (this.insightsSimplified) {
                return this.metric === 'volatility_mad' ? 'Typical variability' : 'Typical reading trend';
            }
            return this.metric === 'volatility_mad'
                ? '30-Day MAD (Median Absolute Deviation)'
                : '30-Day Moving Average Trend';
        },
    },
}
</script>

<style scoped>
.analytics-trend-chart {
    width: 100%;
}
</style>
